import { useEffect, useState } from 'react';
import API from '@/lib/api';
import { APP_VERSION, CHANGELOG, nieuweReleasesSinds } from '@/lib/changelog';
import { FEATURES, mergeFeatures } from '@/lib/features';
import { Sparkle, Rocket, X, CaretDown, CaretUp } from '@phosphor-icons/react';

/**
 * WhatsNewBanner — verschijnt wanneer APP_VERSION > inst.laatst_geziene_versie
 * en feature 'whats_new_banner' actief is. Toont release-samenvatting + lijst
 * van nieuwe features, met "Start mini-tour" en "Begrepen"-knoppen.
 */
export default function WhatsNewBanner({ onStartTour }) {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [releases, setReleases] = useState([]);
  const [features, setFeatures] = useState(() => mergeFeatures({}));

  useEffect(() => {
    let cancelled = false;
    API.get('/instellingen').then(r => {
      if (cancelled) return;
      const inst = r.data || {};
      const feat = (inst.features || {}).whats_new_banner;
      const actief = feat ? feat.actief : true;
      setFeatures(mergeFeatures(inst.features || {}));
      if (!actief) return;
      const nieuw = nieuweReleasesSinds(inst.laatst_geziene_versie || '');
      if (nieuw.length > 0) {
        setReleases(nieuw);
        setVisible(true);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const dismiss = async (alsBegrepen = true) => {
    setVisible(false);
    if (alsBegrepen) {
      try { await API.put('/instellingen', { laatst_geziene_versie: APP_VERSION }); } catch {}
    }
  };

  const startTour = () => {
    // Bundel alle nieuwe feature-keys over alle niet-geziene releases
    const keys = Array.from(new Set(releases.flatMap(r => r.feature_keys)));
    // Filter op features die actief zijn (anders zinloos om te tonen)
    const actieveKeys = keys.filter(k => features[k]?.actief !== false);
    dismiss(true);
    onStartTour?.(actieveKeys);
  };

  if (!visible || releases.length === 0) return null;

  // Totaal nieuwe feature-keys
  const alleKeys = Array.from(new Set(releases.flatMap(r => r.feature_keys)));
  const features_map = Object.fromEntries(FEATURES.map(f => [f.key, f]));
  const nieuweFeatures = alleKeys.map(k => features_map[k]).filter(Boolean);
  const telling = nieuweFeatures.length;

  return (
    <div className="mb-4 border-l-4 border-amber-400 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-sm" data-testid="whats-new-banner">
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 bg-amber-400 text-white flex items-center justify-center">
          <Sparkle size={18} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-bold text-gray-900">
              🎉 Nieuw in versie {releases[0].versie}
            </div>
            <span className="text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 font-semibold">
              {telling} nieuwe functie{telling !== 1 && 's'}
            </span>
            <span className="text-[10px] text-gray-500">· {releases[0].datum}</span>
          </div>
          <div className="text-[12px] text-gray-700 mt-1 leading-relaxed">{releases[0].titel}</div>

          {/* Uitklapbare lijst met nieuwe features */}
          <button
            onClick={() => setExpanded(e => !e)}
            className="mt-1 text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1"
            data-testid="whats-new-toggle"
          >
            {expanded ? <CaretUp size={11} /> : <CaretDown size={11} />}
            {expanded ? 'Verberg lijst' : `Toon alle ${telling} nieuwe functies`}
          </button>

          {expanded && (
            <ul className="mt-2 space-y-1 text-[12px] text-gray-700" data-testid="whats-new-list">
              {nieuweFeatures.map(f => (
                <li key={f.key} className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">▸</span>
                  <div>
                    <strong className="text-gray-900">{f.label}</strong>
                    <span className="text-gray-600"> — {f.beschrijving.split('.')[0]}.</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={startTour}
              className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
              style={{ background: '#D97706' }}
              data-testid="whats-new-start-tour"
            >
              <Rocket size={13} weight="duotone" /> Start mini-tour
            </button>
            <button
              onClick={() => dismiss(true)}
              className="btn btn-ghost btn-sm border border-amber-300 text-amber-900 hover:bg-amber-100"
              data-testid="whats-new-dismiss"
            >
              Begrepen, niet meer tonen
            </button>
          </div>
        </div>
        <button
          onClick={() => dismiss(false)}
          className="flex-shrink-0 text-gray-400 hover:text-gray-900"
          title="Later beslissen (banner verschijnt opnieuw bij volgende bezoek)"
          data-testid="whats-new-close"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
